import { Search, Book, Tag, UserCircle, Loader2, X } from 'lucide-react';
import { useState, useRef, useEffect, forwardRef, type ForwardedRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getImageUrl, handleImageError } from '../utils/imageUtils';

interface SearchSuggestion {
  id: string | number;
  title: string;
  type: 'book' | 'author' | 'category';
  image?: string;
}

interface SearchBarProps {
  isOpen: boolean;
  toggleSearch: () => void;
  closeAllDropdowns: () => void;
}

// Debounce hook for search queries
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SearchBar = forwardRef(({ isOpen, toggleSearch, closeAllDropdowns }: SearchBarProps, ref: ForwardedRef<HTMLDivElement>) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'books' | 'categories' | 'authors' | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search categories
  const searchCategories = [
    { icon: <Book className="w-4 h-4" />, name: "الكتب", filter: 'books' as const },
    { icon: <Tag className="w-4 h-4" />, name: "التصنيفات", filter: 'categories' as const },
    { icon: <UserCircle className="w-4 h-4" />, name: "الكتّاب", filter: 'authors' as const }
  ];

  // Handle filter click - toggle selection
  const handleFilterClick = (filter: 'books' | 'categories' | 'authors') => {
    setActiveFilter(currentFilter => currentFilter === filter ? null : filter);
  };

  // Fetch search results when debounced query changes
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (debouncedSearchQuery.length < 2) {
        setSearchSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const apiUrl = import.meta.env.VITE_API_URL;
        
        // Fetch books for book and category suggestions
        const booksResponse = await axios.get(`${apiUrl}/books`);
        const booksData = booksResponse.data.data || booksResponse.data.books || booksResponse.data;
        
        if (!Array.isArray(booksData)) {
          throw new Error('Invalid response format from server');
        }
        
        // Fetch authors for better author data including images
        const authorsResponse = await axios.get(`${apiUrl}/authors`);
        const authorsData = authorsResponse.data.data || authorsResponse.data.authors || authorsResponse.data;
        
        let results: SearchSuggestion[] = [];
        
        // If no filter is active or 'books' filter is active, include book results
        if (activeFilter === null || activeFilter === 'books') {
          const bookResults = booksData
            .filter(book => 
              book.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
              book.author.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
            )
            .slice(0, 5)
            .map(book => ({
              id: book.id,
              title: book.title,
              type: 'book' as const,
              image: book.image
            }));
          results = [...results, ...bookResults];
        }
        
        // If no filter is active or 'authors' filter is active, include author results
        if (activeFilter === null || activeFilter === 'authors') {
          // Get authors with their images from the authors API
          const authorResults = Array.isArray(authorsData) 
            ? authorsData
                .filter(author => 
                  author.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
                )
                .slice(0, 5)
                .map(author => ({
                  id: author.id || author.name,
                  title: author.name,
                  type: 'author' as const,
                  image: author.image
                }))
            : [];
          
          results = [...results, ...authorResults];
        }
        
        // If no filter is active or 'categories' filter is active, include category results
        if (activeFilter === null || activeFilter === 'categories') {
          const categorySet = new Set<string>();
          booksData.forEach(book => {
            if (book.category && 
                book.category.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) {
              categorySet.add(book.category);
            }
          });
          
          const categoryResults = Array.from(categorySet)
            .slice(0, 5)
            .map(category => ({
              id: category,
              title: category,
              type: 'category' as const
            }));
          
          results = [...results, ...categoryResults];
        }
        
        setSearchSuggestions(results);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setSearchSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSearchResults();
  }, [debouncedSearchQuery, activeFilter]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle search form submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Create a fresh URL with only our search parameters
      const searchParams = new URLSearchParams();
      searchParams.append('q', searchQuery.trim());
      if (activeFilter) {
        searchParams.append('type', activeFilter);
      }
      
      // Navigate to a clean search URL
      navigate({
        pathname: '/search',
        search: searchParams.toString()
      });
      
      closeAllDropdowns();
      setSearchQuery('');
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    // Reset URL before navigating to avoid stale parameters
    switch (suggestion.type) {
      case 'book':
        navigate(`/books/${suggestion.id}`);
        break;
      case 'author':
        // Create a fresh URL for author search
        { const authorParams = new URLSearchParams();
        authorParams.append('author', suggestion.title);
        navigate({
          pathname: '/books',
          search: authorParams.toString()
        });
        break; }
      case 'category':
        // Create a fresh URL for category search
        { const categoryParams = new URLSearchParams();
        categoryParams.append('category', suggestion.title);
        navigate({
          pathname: '/books',
          search: categoryParams.toString()
        });
        break; }
    }
    closeAllDropdowns();
    setSearchQuery('');
  };

  // Group suggestions by type for better organization
  const groupedSuggestions = searchSuggestions.reduce((groups, suggestion) => {
    if (!groups[suggestion.type]) {
      groups[suggestion.type] = [];
    }
    groups[suggestion.type].push(suggestion);
    return groups;
  }, {} as Record<string, SearchSuggestion[]>);

  return (
    <div className="relative" ref={ref}>
      <button 
        onClick={toggleSearch}
        className="p-2.5 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-105"
        aria-label="البحث"
      >
        <Search className="w-5 h-5 text-gray-600" />
      </button>
      
      {isOpen && (
        <div className="fixed inset-x-0 top-16 mx-2 z-50 max-w-sm w-full rounded-xl shadow-xl border border-gray-200 bg-white md:absolute md:inset-x-auto md:top-full md:left-0 md:mt-2 md:w-80"
        style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="p-4">
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
              {searchCategories.map((category) => (
                <button
                  key={category.filter}
                  onClick={() => handleFilterClick(category.filter)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full hover:bg-orange-50 hover:text-orange-500 transition-all duration-200 hover:scale-105 flex-shrink-0 ${
                    activeFilter === category.filter 
                      ? 'bg-orange-50 text-orange-500 border border-orange-200'
                      : 'bg-gray-50 text-gray-700 border border-gray-100'
                  }`}
                >
                  {category.icon}
                  {category.name}
                </button>
              ))}
            </div>
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ابحث عن كتاب، كاتب، أو تصنيف..."
                className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50"
                >
                  <X className="w-5 h-5" />
                </button>
              ) : (
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              )}
            </form>
            
            {/* Search Suggestions */}
            {searchQuery.length > 0 && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                {isLoading ? (
                  <div className="flex justify-center items-center py-6">
                    <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                  </div>
                ) : searchSuggestions.length > 0 ? (
                  <div className="space-y-4">
                    {/* Display books if available */}
                    {groupedSuggestions.book && groupedSuggestions.book.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">الكتب</p>
                        <div className="space-y-2">
                          {groupedSuggestions.book.map((suggestion) => (
                            <button
                              key={suggestion.id.toString()}
                              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-right"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              <img 
                                src={getImageUrl(suggestion.image)}
                                alt="" 
                                className="w-8 h-10 object-cover rounded" 
                                onError={handleImageError()}
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{suggestion.title}</p>
                                <p className="text-xs text-gray-500">كتاب</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Display authors if available */}
                    {groupedSuggestions.author && groupedSuggestions.author.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">الكتّاب</p>
                        <div className="space-y-2">
                          {groupedSuggestions.author.map((suggestion) => (
                            <button
                              key={suggestion.id.toString()}
                              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-right"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              {suggestion.image ? (
                                <img 
                                  src={getImageUrl(suggestion.image)}
                                  alt={suggestion.title} 
                                  className="w-8 h-8 object-cover rounded-full" 
                                  onError={handleImageError('/placeholder-author.png')}
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <UserCircle className="w-5 h-5 text-blue-500" />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{suggestion.title}</p>
                                <p className="text-xs text-gray-500">كاتب</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Display categories if available */}
                    {groupedSuggestions.category && groupedSuggestions.category.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">التصنيفات</p>
                        <div className="space-y-2">
                          {groupedSuggestions.category.map((suggestion) => (
                            <button
                              key={suggestion.id.toString()}
                              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-right"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <Tag className="w-5 h-5 text-green-500" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{suggestion.title}</p>
                                <p className="text-xs text-gray-500">تصنيف</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">لا توجد نتائج لبحثك</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;